import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  FileText,
  Plus,
  Loader2,
  Trash2,
  ShieldAlert,
  ThumbsUp,
  ThumbsDown,
  AlertTriangle,
  Mic,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  listWriteups,
  addWriteup,
  deleteWriteup,
  WRITEUP_CATEGORIES,
} from '@/lib/crewWriteupsData';
import { cn } from '@/lib/utils';

const todayIso = () => new Date().toISOString().slice(0, 10);

/* ----------------------------------------------------------------------------
 * Category visuals — kept inside this file so a category palette change is
 * a one-file edit.
 * ------------------------------------------------------------------------- */
const CATEGORY_META = {
  positive: {
    tone: 'border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]',
    Icon: ThumbsUp,
  },
  negative: {
    tone: 'border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]',
    Icon: ThumbsDown,
  },
  verbal_warning: {
    tone: 'border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/25 text-[hsl(var(--warning))]',
    Icon: Mic,
  },
  written_warning: {
    tone: 'border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))]',
    Icon: AlertTriangle,
  },
};

/**
 * WriteupsSheet — per-employee admin log. Renders the existing entries
 * newest-first and provides an inline form to append a new entry.
 *
 * The component is intentionally self-contained: it owns the list state
 * + the form state, and only depends on `crewMember` for context. When
 * the sheet is closed and reopened on a different employee, both are reset.
 */
export const WriteupsSheet = ({ open, onOpenChange, crewMember }) => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null); // delete spinner per row

  // New-entry form state
  const [formDate, setFormDate] = useState(todayIso());
  const [formCategory, setFormCategory] = useState('positive');
  const [formNote, setFormNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const crewId = crewMember?.id;

  const load = useCallback(async () => {
    if (!crewId) return;
    setLoading(true);
    setError(null);
    try {
      const list = await listWriteups(crewId);
      setEntries(list);
    } catch (e) {
      const msg = e?.message || t('settings.crewWriteups.loadFailed');
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [crewId, t]);

  useEffect(() => {
    if (open && crewId) {
      // Reset the form to "blank, today, positive" every time the sheet
      // is reopened on a different employee.
      setFormDate(todayIso());
      setFormCategory('positive');
      setFormNote('');
      load();
    }
  }, [open, crewId, load]);

  const handleSubmit = async () => {
    if (!crewId) return;
    if (!formNote.trim()) {
      toast.error(t('settings.crewWriteups.noteRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const created = await addWriteup({
        crewId,
        date: formDate,
        category: formCategory,
        note: formNote,
      });
      // Optimistic prepend (sorted newest-first; user-entered `date` may
      // not equal today so we re-sort after insert for correctness).
      setEntries((cur) => {
        const next = [created, ...cur];
        next.sort((a, b) => {
          const cmp = String(b.date).localeCompare(String(a.date));
          if (cmp !== 0) return cmp;
          return String(b.created_at).localeCompare(String(a.created_at));
        });
        return next;
      });
      setFormNote('');
      toast.success(t('settings.crewWriteups.savedToast'));
    } catch (e) {
      const msg =
        e?.message?.includes('row-level security') ||
        e?.code === '42501'
          ? t('settings.crewWriteups.adminRequired')
          : e?.message || t('settings.crewWriteups.saveFailed');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    setBusyId(id);
    try {
      await deleteWriteup(id);
      setEntries((cur) => cur.filter((e) => e.id !== id));
      toast.success(t('settings.crewWriteups.deletedToast'));
    } catch (e) {
      toast.error(e?.message || t('settings.crewWriteups.deleteFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const counts = useMemo(() => {
    const c = { positive: 0, negative: 0, verbal_warning: 0, written_warning: 0 };
    for (const e of entries) {
      if (c[e.category] != null) c[e.category] += 1;
    }
    return c;
  }, [entries]);

  if (!crewMember) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg bg-[hsl(var(--card))] border-border overflow-y-auto"
        data-testid="writeups-sheet"
      >
        <SheetHeader className="pb-3">
          <SheetTitle className="flex items-center gap-2 text-foreground">
            <FileText size={16} className="text-[hsl(var(--primary))]" />
            {t('settings.crewWriteups.title', { name: crewMember.name })}
          </SheetTitle>
          <SheetDescription className="text-muted-foreground text-xs">
            {t('settings.crewWriteups.subtitle')}
          </SheetDescription>
          <div
            className="flex flex-wrap gap-1.5 pt-2 text-[10px] uppercase tracking-[0.16em]"
            data-testid="writeups-summary"
          >
            {WRITEUP_CATEGORIES.map((cat) => {
              const { Icon, tone } = CATEGORY_META[cat];
              return (
                <Badge
                  key={cat}
                  variant="outline"
                  className={cn('border', tone, 'gap-1.5')}
                >
                  <Icon size={9} />
                  {t(`settings.crewWriteups.categories.${cat}`)} · {counts[cat]}
                </Badge>
              );
            })}
          </div>
        </SheetHeader>
        <Separator className="bg-border" />

        {/* ---------- Add entry form ---------- */}
        <section
          className="space-y-3 py-4 border-b border-border"
          data-testid="writeups-add-form"
        >
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                {t('settings.crewWriteups.fields.date')}
              </Label>
              <Input
                type="date"
                value={formDate}
                onChange={(e) => setFormDate(e.target.value)}
                className="h-9 mt-1 bg-[hsl(var(--background))] border-border text-foreground"
                data-testid="writeups-form-date"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                {t('settings.crewWriteups.fields.category')}
              </Label>
              <Select value={formCategory} onValueChange={setFormCategory}>
                <SelectTrigger
                  className="h-9 mt-1 bg-[hsl(var(--background))] border-border text-foreground"
                  data-testid="writeups-form-category"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--card))] border-border">
                  {WRITEUP_CATEGORIES.map((cat) => (
                    <SelectItem
                      key={cat}
                      value={cat}
                      data-testid={`writeups-cat-${cat}`}
                    >
                      {t(`settings.crewWriteups.categories.${cat}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {t('settings.crewWriteups.fields.note')}
            </Label>
            <Textarea
              value={formNote}
              onChange={(e) => setFormNote(e.target.value)}
              placeholder={t('settings.crewWriteups.fields.notePlaceholder')}
              rows={3}
              className="mt-1 bg-[hsl(var(--background))] border-border text-foreground"
              data-testid="writeups-form-note"
            />
          </div>
          <div className="flex justify-end">
            <Button
              onClick={handleSubmit}
              disabled={submitting || !formNote.trim()}
              className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
              data-testid="writeups-submit"
            >
              {submitting ? (
                <Loader2 size={13} className="mr-1.5 animate-spin" />
              ) : (
                <Plus size={13} className="mr-1.5" />
              )}
              {t('settings.crewWriteups.addEntry')}
            </Button>
          </div>
        </section>

        {/* ---------- Existing entries (newest first) ---------- */}
        <section className="py-4">
          <h4 className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">
            {t('settings.crewWriteups.historyTitle')}
          </h4>
          {error ? (
            <div
              className="text-xs text-[hsl(var(--destructive))] border border-[hsl(var(--destructive))]/30 rounded-md px-3 py-2 bg-[hsl(var(--destructive))]/10 flex items-center gap-2"
              data-testid="writeups-error"
            >
              <ShieldAlert size={13} />
              {error}
            </div>
          ) : loading ? (
            <div className="text-xs text-muted-foreground py-3 text-center">
              <Loader2
                size={16}
                className="inline-block animate-spin mr-2 align-text-bottom"
              />
              {t('settings.crewWriteups.loading')}
            </div>
          ) : entries.length === 0 ? (
            <div
              className="text-xs text-muted-foreground border border-dashed border-border rounded-md py-6 text-center"
              data-testid="writeups-empty"
            >
              {t('settings.crewWriteups.empty')}
            </div>
          ) : (
            <ul className="space-y-2" data-testid="writeups-list">
              {entries.map((e) => {
                const { Icon, tone } = CATEGORY_META[e.category] || {};
                const isBusy = busyId === e.id;
                return (
                  <li
                    key={e.id}
                    className="border border-border rounded-md bg-[hsl(var(--muted))]/40 px-3 py-2.5"
                    data-testid={`writeups-row-${e.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex flex-wrap items-center gap-2 min-w-0">
                        <Badge
                          variant="outline"
                          className={cn(
                            'uppercase text-[9px] tracking-[0.16em] border',
                            tone
                          )}
                          data-testid={`writeups-row-cat-${e.id}`}
                        >
                          {Icon ? <Icon size={9} className="mr-1.5" /> : null}
                          {t(`settings.crewWriteups.categories.${e.category}`)}
                        </Badge>
                        <span className="text-xs text-foreground font-semibold tabular-nums">
                          {e.date}
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          ·{' '}
                          {t('settings.crewWriteups.byAuthor', {
                            author: e.author_name,
                          })}
                        </span>
                      </div>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => handleDelete(e.id)}
                        disabled={isBusy}
                        className="h-7 w-7 text-muted-foreground hover:text-[hsl(var(--destructive))]"
                        aria-label={t('settings.crewWriteups.deleteAria')}
                        data-testid={`writeups-delete-${e.id}`}
                      >
                        {isBusy ? (
                          <Loader2 size={11} className="animate-spin" />
                        ) : (
                          <Trash2 size={11} />
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-foreground/90 mt-1.5 leading-relaxed whitespace-pre-wrap">
                      {e.note}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </SheetContent>
    </Sheet>
  );
};

export default WriteupsSheet;
